/**
 * TypeScript types matching the WASM module exports.
 * These mirror the Rust types in src/wasm/types.rs
 */

/** Options for converting a CUBE file to XMP format */
export interface WasmConvertOptions {
  /** CUBE file content as string (UTF-8) */
  cube_content: string;
  
  /** Original filename (used for output naming if no custom name) */
  filename: string;
  
  /** Custom profile name (optional, overrides TITLE directive) */
  name?: string;
  
  /** Short display name (optional) */
  short_name?: string;
  
  /** Sort name for ordering (optional) */
  sort_name?: string;
  
  /** Group label for organization (optional) */
  group?: string;
  
  /** Profile description (optional) */
  description?: string;
  
  /** Target LUT size 2-64 (optional, default: min(source, 32)) */
  lut_size?: number;
  
  /** Requires RGB Tables flag (default: false) */
  requires_rgb_tables: boolean;
  
  /** Show in Presets (default: true) */
  show_in_presets: boolean;
  
  /** Show in Quick Actions (default: false) */
  show_in_quick_actions: boolean;
  
  /** Allow LUT sizes >32 without error (default: false) */
  allow_large: boolean;
}

/** Default conversion options */
export const DEFAULT_CONVERT_OPTIONS: Omit<WasmConvertOptions, 'cube_content' | 'filename'> = {
  requires_rgb_tables: false,
  show_in_presets: true,
  show_in_quick_actions: false,
  allow_large: false,
};

/** Non-fatal warning from conversion or validation */
export interface WasmWarning {
  /** Warning code (e.g., 'extended_range', 'downsampled') */
  code: string;
  /** Human-readable warning message */
  message: string;
}

/** Successful conversion result */
export interface WasmConvertSuccess {
  type: 'success';
  /** Generated XMP file content (UTF-8 string) */
  xmp_content: string;
  /** Non-fatal warnings */
  warnings: WasmWarning[];
  /** Conversion time in milliseconds */
  duration_ms: number;
}

/** Failed conversion result */
export interface WasmConvertError {
  type: 'error';
  /** Error code for programmatic handling */
  code: string;
  /** Human-readable error message */
  message: string;
}

/** Result of a conversion operation - discriminated union */
export type WasmConvertResult = WasmConvertSuccess | WasmConvertError;

/** Successful validation result */
export interface WasmValidationValid {
  type: 'valid';
  /** Detected LUT size (e.g., 17, 33, 64) */
  lut_size: number;
  /** Profile title from TITLE directive (if present) */
  title?: string;
  /** Warnings about file content */
  warnings: WasmWarning[];
}

/** Invalid validation result */
export interface WasmValidationInvalid {
  type: 'invalid';
  /** Error code */
  code: string;
  /** Human-readable error message */
  message: string;
}

/** Result of a validation operation - discriminated union */
export type WasmValidationResult = WasmValidationValid | WasmValidationInvalid;

/** Type guard for successful conversion */
export function isConvertSuccess(result: WasmConvertResult): result is WasmConvertSuccess {
  return result.type === 'success';
}

/** Type guard for conversion error */
export function isConvertError(result: WasmConvertResult): result is WasmConvertError {
  return result.type === 'error';
}

/** Type guard for valid file */
export function isValidationValid(result: WasmValidationResult): result is WasmValidationValid {
  return result.type === 'valid';
}

/** Type guard for invalid file */
export function isValidationInvalid(result: WasmValidationResult): result is WasmValidationInvalid {
  return result.type === 'invalid';
}
